import { Option } from '@fullstory/components/components/RadioOption';
import {
  DependentEventFilter,
  Event,
  EventFilter,
  NativePlatform,
  PlatformSelector,
} from '@fullstory/proto/fs/api/segment';
import * as segment from '@fullstory/proto/fs/api/segment';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { ObjectCollection } from '@fullstory/renderer-shared';
import React, { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { PrimaryValue } from '../types';

export const ELEMENT_GROUP_PREFIX = 'ne::';

export const SignalStudioContext =
  React.createContext<React.ComponentType<SignalStudioComponentProps> | null>(null);

export interface SignalStudioPreviewSearchProps {
  eventFilters: (EventFilter | null)[]; // in priority order, will try event filters in order until a preview is found
  primary: PrimaryValue;
  onPrimaryChange: (value: PrimaryValue) => void;
  refinements: DependentEventFilter[];
  onRefinementsChange: (refinements: DependentEventFilter[]) => void;
  fallbackRefinements: DependentEventFilter[];
  implicitRefinements?: DependentEventFilter[];
}

export interface SignalStudioPreviewResolvedEvent {
  userId: string;
  sessionId: string;
  pageId: string;
  eventPageOffset: number;
  nativePlatform: segment.NativePlatform;
}

export type SignalStudioPreviewMode =
  /**
   * The preview presents a page in interactive mode and allows the user to
   * inspect the elements it contains.
   */
  | 'element-inspector'
  /**
   * The preview presents an event with full playback fidelity. In this mode,
   * the preview is not interactive; it cannot be scrolled or inspected.
   */
  | 'event-preview'
  /**
   * The preview presents a page in interactive mode and allows the user to
   * scroll the page to explore it.
   */
  | 'page-preview';

export type SignalStudioHighlightType =
  /** A highlight style for elements that are being inspected directly. */
  | 'inspected'
  /** A highlight style for elements that provide context for the inspected elements. */
  | 'inspected-context';

export type SignalStudioHighlight = {
  type: SignalStudioHighlightType;
  target: PlatformSelector;
  includeInnerText?: boolean;
};

export type SignalStudioHighlightConstraint = {
  type: SignalStudioHighlightType;
  constrainedTo: SignalStudioHighlightType;
  nested?: boolean; // if true, the constrained type must be nested within the constrainedTo type (i.e. cannot be at the same level)
};

export type SignalStudioHighlightConstraints = {
  constraints?: SignalStudioHighlightConstraint[];
  requireLeastDepth?: boolean;
  constrainInspected?: boolean; // if true, inspected hover rects and clicked inspected items must be within the inspected group
};

interface GeneralSignalStudioPreviewProps {
  visualUnsupportedMessage?: string;
}

interface WithSearch extends GeneralSignalStudioPreviewProps {
  search: SignalStudioPreviewSearchProps;
  event?: never;
}

interface WithEvent extends GeneralSignalStudioPreviewProps {
  search?: never;
  event: Event | null;
}

export type SignalStudioPreviewProps = WithSearch | WithEvent;

export type SignalStudioPreviewElementProps = {
  constrainedTo?: SignalStudioHighlightType;
};

export type SignalStudioPreviewViewAllElementsProps = {
  supported: boolean;
  initialValue?: boolean;
  onClick?: (element: NamedElementRecord) => void;
};

export type SignalStudioPreviewOnHighlightObjects = (
  objects: ObjectCollection,
  unconstrainedObjects: ObjectCollection,
) => void;

export interface SignalStudioComponentProps {
  archived?: boolean;
  header: string;
  backMessage?: string;
  lowerConfigPartName: string;
  configSidebar?: ReactElement;
  configSidebarMeta?: ReactElement;
  disableEdit?: boolean;
  disableArchive?: boolean;
  tabs?: {
    titles: string[];
    content: { key: string; node: ReactElement }[];
  };
  preview: {
    // state, when provided, determines when preview state should change
    // use this for preview stability as needed
    // when not provided, preview state will change anytime the relevant search or refinements change
    // note: previews are eligible to change regardless when there is one of the following conditions
    //       no preview event yet, the current preview is not found, or the "get new image" button has been triggered
    state?: number | string;
    // initialPreview, if set, is used to load the initial preview until the preview state changes
    initialPreview?: SignalStudioPreviewResolvedEvent;
    // initialPreviewState, if set, is used to force load the initial preview on change
    initialPreviewState?: number | string;
    index: number;
    onIndexChange: (index: number) => void;
    mode: SignalStudioPreviewMode;
    previews: SignalStudioPreviewProps[];
    highlights?: SignalStudioHighlight[];
    highlightConstraints?: SignalStudioHighlightConstraints;
    elements?: SignalStudioPreviewElementProps;
    viewAllElements?: SignalStudioPreviewViewAllElementsProps;
    onHighlightedObjects?: SignalStudioPreviewOnHighlightObjects;
    onInspectSelector?: (
      selector: string,
      canonicalizedSelector: string,
      nativePlatform: NativePlatform | undefined,
    ) => void;
    onRandomPreview?: () => void;
  };
  emptyMessage: ReactNode | null; // Will be null for API events
  metadata?: {
    createdBy: string;
    updatedBy: string;
    createdAt: timestamp | null;
    updatedAt: timestamp | null;
  };
  onArchive?: () => void;
  onUnarchive?: () => void;
  onRejectSuggestion?: (feedback: string) => void;
  rejectionOptions?: {
    // State that when provided, will render a preset list of
    // feedback options for when the suggestion is rejected.
    feedbackOptions: Option<string>[];
    onSelectFeedbackOption: (feedbackOption: Option<string>) => void;
    selectedFeedbackOption: Option<string>;
    shouldShowFeedbackFreeText?: boolean;
  };
  isSuggestion?: boolean;
  fullScreen?: boolean; // defaults to true
  hasUnsavedChanges?: boolean; // If set and true, will show a confirm prompt before closing
  onCloseSignal: () => void;
  onConfirm?: () => void;
  confirmLabel?: string; // defaults to "Save"
}

interface SignalStudioProviderProps {
  component: React.ComponentType<SignalStudioComponentProps>;
}

export const SignalStudioProvider: React.FC<PropsWithChildren<SignalStudioProviderProps>> = ({
  component,
  children,
}) => <SignalStudioContext.Provider value={component}>{children}</SignalStudioContext.Provider>;
